import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Lock, Pencil } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { clientApi, fileUrl } from '../../api/client';
import StatusBadge from '../../components/dashboard/StatusBadge';
import ProfileView from '../../components/dashboard/ProfileView';
import ProfileForm from '../../components/dashboard/ProfileForm';
import PasswordChangeForm from '../../components/dashboard/PasswordChangeForm';
import DashModal from '../../components/dashboard/DashModal';
import DashToolbar from '../../components/dashboard/DashToolbar';
import Pagination, { usePagination } from '../../components/dashboard/Pagination';

const TIMELINE = [
  'request_sent',
  'quote_sent',
  'quote_accepted',
  'paid',
  'translator_assigned',
  'translation_in_progress',
  'lawyer_notary_in_progress',
  'verification_validated',
  'final_control',
  'ready_to_deliver',
  'delivered',
  'completed',
];

function OrdersTable({ rows, labels }) {
  const { t } = useLanguage();
  if (!rows.length) return <p className="dash-empty">{t.dashboard.empty}</p>;
  return (
    <div className="dash-table-wrap">
      <table className="dash-table">
        <thead>
          <tr>
            <th>{labels.colId}</th>
            <th>{labels.colDocument}</th>
            <th>{labels.colLangs}</th>
            <th>{labels.colStatus}</th>
            <th>{labels.colPrice}</th>
            <th>{labels.colDate}</th>
            <th>{t.dashboard.actions}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id}>
              <td>{o.id}</td>
              <td>{o.document}</td>
              <td>{o.langs}</td>
              <td><StatusBadge status={o.status} labels={t.dashboard.status} /></td>
              <td>{o.price} {o.currency || 'TND'}</td>
              <td>{o.date}</td>
              <td>
                <Link className="dash-link" to={`/app/orders/${o.id}`}>{t.dashboard.view}</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ClientHomePage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [data, setData] = useState(null);
  const [howOpen, setHowOpen] = useState(false);

  useEffect(() => {
    clientApi.overview().then(setData).catch(() => setData(null));
  }, []);

  const stats = data?.stats || { orders: 0, quotes: 0, ready: 0, inProgress: 0 };

  return (
    <>
      <header className="dash-page-head">
        <h1>{c.homeTitle}</h1>
        <p>{c.homeSubtitle}</p>
      </header>

      <div className="dash-kpi-grid">
        <div className="dash-kpi"><em>{c.statOrders}</em><strong>{stats.orders}</strong><small>{c.kpiAll}</small></div>
        <div className="dash-kpi"><em>{c.statProgress}</em><strong>{stats.inProgress}</strong><small>{c.kpiProgress}</small></div>
        <div className="dash-kpi"><em>{c.statReady}</em><strong>{stats.ready}</strong><small>{c.kpiReady}</small></div>
        <div className="dash-kpi"><em>{c.statQuotes}</em><strong>{stats.quotes}</strong><small>{c.kpiQuotes}</small></div>
      </div>

      <div className="dash-info-grid">
        <article className="dash-info-card">
          <h3>{d.clientInfoTitle}</h3>
          <p>{d.clientInfoOrders}</p>
        </article>
        <article className="dash-info-card">
          <h3>{d.deliverables}</h3>
          <p>{d.clientInfoFiles}</p>
          <button type="button" className="dash-btn-sm" style={{ marginTop: 10 }} onClick={() => setHowOpen(true)}>
            {d.howItWorks}
          </button>
        </article>
      </div>

      <div className="dash-actions">
        <Link to="/order" className="btn btn-primary">{c.ctaOrder}</Link>
        <Link to="/contact" className="btn btn-outline">{c.ctaQuote}</Link>
      </div>
      <section className="dash-panel">
        <h2>{c.recentOrders}</h2>
        <OrdersTable rows={data?.recentOrders || []} labels={c} />
      </section>

      <DashModal open={howOpen} title={d.howItWorks} onClose={() => setHowOpen(false)}>
        <ol style={{ margin: 0, paddingInlineStart: 18, lineHeight: 1.7, color: 'var(--gray-700)' }}>
          <li>Vous commandez en ligne (connecté).</li>
          <li>L’admin assigne un traducteur selon <strong>De → Vers</strong>.</li>
          <li>Le traducteur traite puis livre le document.</li>
          <li>Vous le téléchargez dans le détail de commande.</li>
        </ol>
      </DashModal>
    </>
  );
}

export function ClientOrdersPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    clientApi.orders().then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));
  }, []);

  const filtered = useMemo(() => orders.filter((o) => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [o.id, o.document, o.langs].some((x) => String(x || '').toLowerCase().includes(q));
  }), [orders, search, statusFilter]);
  const ordersPg = usePagination(filtered, 6);

  return (
    <>
      <header className="dash-page-head">
        <h1>{c.ordersTitle}</h1>
        <p>{c.ordersSubtitle}</p>
      </header>
      <DashToolbar
        search={search}
        onSearch={setSearch}
        searchPlaceholder={c.searchOrders}
        filters={[
          { id: 'all', label: c.filterAll },
          { id: 'pending', label: d.status.pending },
          { id: 'in_progress', label: d.status.in_progress },
          { id: 'ready', label: d.status.ready },
          { id: 'delivered', label: d.status.delivered },
        ]}
        activeFilter={statusFilter}
        onFilter={setStatusFilter}
      />
      <section className="dash-panel">
        <OrdersTable rows={ordersPg.pageItems} labels={c} />
        <Pagination page={ordersPg.page} totalPages={ordersPg.totalPages} total={ordersPg.total} onChange={ordersPg.setPage} />
      </section>
    </>
  );
}

export function ClientOrderDetailPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    clientApi.order(id).then(setData).catch(() => setData(null));
  }, [id]);

  const pay = async () => {
    setPaying(true);
    setMsg('');
    try {
      await clientApi.createPayment({
        orderId: id,
        amount: order.price,
        currency: order.currency,
        method: 'card',
        success: true,
      });
      const res = await clientApi.order(id);
      setData(res);
      setMsg(c.paymentSuccess || 'Paiement confirmé');
    } catch (err) {
      setMsg(err.data?.error || c.paymentError || 'Erreur de paiement');
    } finally {
      setPaying(false);
    }
  };

  const order = data?.order;
  const statusIndex = TIMELINE.indexOf(order?.status);

  return (
    <>
      <header className="dash-page-head">
        <Link to="/app/orders" className="dash-link">{c.backOrders || d.backSite}</Link>
        <h1>{c.detailTitle || d.orderDetail} {order ? `— ${order.id}` : ''}</h1>
        <p>{c.detailSubtitle || c.ordersSubtitle}</p>
      </header>

      {!order ? (
        <p className="dash-empty">{d.empty}</p>
      ) : (
        <>
          <div className="dash-stats">
            <div className="dash-stat"><span>{c.colDocument}</span><strong style={{ fontSize: '1.1rem' }}>{order.document}</strong></div>
            <div className="dash-stat"><span>{c.colLangs}</span><strong style={{ fontSize: '1.1rem' }}>{order.langs}</strong></div>
            <div className="dash-stat"><span>{c.colPrice}</span><strong style={{ fontSize: '1.1rem' }}>{order.price} TND</strong></div>
          </div>

          {order.paymentStatus !== 'paid' && order.price > 0 && (
            <section className="dash-panel">
              <h2>{c.payment || 'Paiement'}</h2>
              <p>{c.paymentAmount || 'Montant à régler'} : <strong>{order.price} TND</strong></p>
              <button type="button" className="btn btn-primary" disabled={paying} onClick={pay}>
                {paying ? '…' : c.payNow || 'Payer maintenant'}
              </button>
              {msg && <p className="dash-toast">{msg}</p>}
            </section>
          )}

          <section className="dash-panel">
            <h2>{d.timeline}</h2>
            <ol className="dash-timeline">
              {TIMELINE.map((s, i) => (
                <li key={s} className={i <= statusIndex ? 'is-done' : ''}>
                  <StatusBadge status={s} labels={d.status} />
                </li>
              ))}
            </ol>
            <p style={{ marginTop: 12 }}>
              {c.colStatus}: <StatusBadge status={order.status} labels={d.status} />
            </p>
          </section>

          <section className="dash-panel" style={{ marginTop: 16 }}>
            <h2>{d.deliverables}</h2>
            {(data.deliverables || []).length === 0 ? (
              <p className="dash-empty">{d.empty}</p>
            ) : (
              <ul className="dash-file-list">
                {data.deliverables.map((f) => (
                  <li key={f.id}>
                    <span>{f.name}</span>
                    {f.url && (
                      <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="dash-link">
                        PDF
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </>
  );
}

export function ClientQuotesPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState('');
  const quotesPg = usePagination(quotes, 6);
  const [msg, setMsg] = useState('');

  const reload = () => clientApi.quotes().then((res) => setQuotes(res.quotes || [])).catch(() => setQuotes([]));

  useEffect(() => {
    reload();
  }, []);

  const respond = async (id, decision) => {
    setBusy(id + decision);
    setMsg('');
    try {
      await clientApi.respondQuote(id, decision);
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || d.empty);
    } finally {
      setBusy('');
    }
  };

  const pay = async (q) => {
    setBusy(q.id + 'pay');
    setMsg('');
    try {
      await clientApi.createPayment({
        orderId: q.orderId,
        quoteId: q._id,
        amount: q.estimatedPrice,
        currency: q.currency,
        method: 'card',
        success: true,
      });
      setMsg(c.paymentSuccess || 'Paiement confirmé');
      reload();
    } catch (err) {
      setMsg(err.data?.error || c.paymentError || 'Erreur de paiement');
    } finally {
      setBusy('');
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{c.quotesTitle}</h1>
        <p>{c.quotesSubtitle}</p>
      </header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>{c.colId}</th>
                <th>{c.colType}</th>
                <th>{c.colLangs}</th>
                <th>{c.colPrice}</th>
                <th>{c.colStatus}</th>
                <th>{c.colDate}</th>
                <th>{d.actions}</th>
              </tr>
            </thead>
            <tbody>
              {quotesPg.pageItems.map((q) => {
                const canRespond = ['sent', 'answered'].includes(q.status) && q.estimatedPrice != null;
                const canPay = q.status === 'accepted' && q.estimatedPrice != null;
                return (
                  <tr key={q.id}>
                    <td>{q.id}</td>
                    <td>{q.type}</td>
                    <td>{q.langs}</td>
                    <td>{q.estimatedPrice != null ? `${q.estimatedPrice} ${q.currency || 'TND'}` : '—'}</td>
                    <td><StatusBadge status={q.status} labels={d.status} /></td>
                    <td>{q.date}</td>
                    <td>
                      {canRespond ? (
                        <div className="dash-actions" style={{ margin: 0, gap: 6 }}>
                          <button
                            type="button"
                            className="dash-btn-sm"
                            disabled={Boolean(busy)}
                            onClick={() => respond(q.id, 'accepted')}
                          >
                            {c.acceptQuote}
                          </button>
                          <button
                            type="button"
                            className="dash-btn-sm"
                            disabled={Boolean(busy)}
                            onClick={() => respond(q.id, 'rejected')}
                          >
                            {c.rejectQuote}
                          </button>
                        </div>
                      ) : canPay ? (
                        <button
                          type="button"
                          className="dash-btn-sm dash-btn-sm--primary"
                          disabled={Boolean(busy)}
                          onClick={() => pay(q)}
                        >
                          {c.payNow || 'Payer'}
                        </button>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={quotesPg.page} totalPages={quotesPg.totalPages} total={quotesPg.total} onChange={quotesPg.setPage} />
      </section>
    </>
  );
}

export function ClientInvoicesPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [invoices, setInvoices] = useState([]);
  const invoicesPg = usePagination(invoices, 6);

  useEffect(() => {
    clientApi.invoices().then((res) => setInvoices(res.invoices || [])).catch(() => setInvoices([]));
  }, []);

  return (
    <>
      <header className="dash-page-head">
        <h1>{c.invoicesTitle}</h1>
        <p>{c.invoicesSubtitle}</p>
      </header>
      <section className="dash-panel">
        {!invoices.length ? (
          <p className="dash-empty">{d.empty}</p>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table">
              <thead>
                <tr>
                  <th>{c.colId}</th>
                  <th>{c.colDocument}</th>
                  <th>{c.colPrice}</th>
                  <th>{c.colStatus}</th>
                  <th>{c.colDate}</th>
                </tr>
              </thead>
              <tbody>
                {invoicesPg.pageItems.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.id}</td>
                    <td>{inv.orderCode || '—'}</td>
                    <td>{inv.amount} {inv.currency || 'TND'}</td>
                    <td><StatusBadge status={inv.status} labels={d.status} /></td>
                    <td>{inv.issuedAt || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={invoicesPg.page} totalPages={invoicesPg.totalPages} total={invoicesPg.total} onChange={invoicesPg.setPage} />
      </section>
    </>
  );
}

export function ClientMessagesPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [body, setBody] = useState('');
  const [toSel, setToSel] = useState('');
  const [threadKey, setThreadKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const pros = useMemo(
    () => (orders || []).filter((o) => o.translatorId).map((o) => ({ tid: o.translatorId, oid: o._id, label: `${o.translator || 'Pro'} · ${o.id}` })),
    [orders]
  );

  const threads = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      if (!map.has(m.threadKey)) map.set(m.threadKey, { key: m.threadKey, people: new Map(), last: m.createdAt });
      const thread = map.get(m.threadKey);
      [m.from, m.to].forEach((p) => {
        if (p && p.role !== 'client') thread.people.set(p.id, p);
      });
      if (new Date(m.createdAt) > new Date(thread.last || 0)) thread.last = m.createdAt;
    });
    return Array.from(map.values()).sort((x, y) => new Date(y.last || 0) - new Date(x.last || 0));
  }, [messages]);

  const threadLabel = (thread) => {
    const names = Array.from(thread.people.values()).map((p) => (p.role === 'admin' ? c.administration : p.name)).filter(Boolean);
    return names.length ? names.join(' ↔ ') : thread.key;
  };

  const reload = () => clientApi.messages().then((res) => setMessages(res.messages || [])).catch(() => setMessages([]));

  useEffect(() => {
    reload();
    clientApi.orders().then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));
  }, []);

  const pickThread = (key) => {
    setThreadKey(key);
    if (key?.startsWith('order:')) {
      const pro = pros.find((p) => `order:${p.oid}` === key);
      setToSel(pro ? `${pro.tid}|${pro.oid}` : '');
    } else {
      setToSel('');
    }
  };

  const filtered = threadKey ? messages.filter((m) => m.threadKey === threadKey) : messages;
  const msgsPg = usePagination(filtered, 6);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) {
      setMsg(t.auth?.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const [tid, oid] = toSel ? toSel.split('|') : [null, null];
      await clientApi.sendMessage({ body, ...(tid ? { toId: tid, orderId: oid, threadKey: `order:${oid}` } : {}) });
      setBody('');
      setMsg(d.saved);
      reload();
    } catch (err) {
      setMsg(err.data?.error || d.empty);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{c.messagesTitle}</h1>
        <p>{c.messagesSubtitle}</p>
      </header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        {threads.length > 1 && (
          <div className="dash-field" style={{ marginBottom: 12 }}>
            <label>{t.dashboard.admin?.thread || 'Fil'}</label>
            <select value={threadKey} onChange={(e) => pickThread(e.target.value)}>
              <option value="">{t.dashboard.admin?.allThreads || 'Toutes'}</option>
              {threads.map((thread) => <option key={thread.key} value={thread.key}>{threadLabel(thread)}</option>)}
            </select>
          </div>
        )}
        <div className="dash-chat">
          {filtered.length === 0 ? (
            <p className="dash-empty">{d.empty}</p>
          ) : (
            msgsPg.pageItems.map((m) => (
              <div key={m.id} className={`dash-chat-bubble${m.from?.role === 'client' ? ' is-mine' : ''}`}>
                <strong>{m.from?.name || '—'}</strong>
                <p>{m.body}</p>
                <small>{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</small>
              </div>
            ))
          )}
        </div>
        <Pagination page={msgsPg.page} totalPages={msgsPg.totalPages} total={msgsPg.total} onChange={msgsPg.setPage} />
        <form className="dash-form" onSubmit={send} noValidate style={{ marginTop: 16, maxWidth: '100%' }}>
          {pros.length > 0 && (
            <div className="dash-field">
              <label>{c.recipient}</label>
              <select value={toSel} onChange={(e) => setToSel(e.target.value)}>
                <option value="">{c.administration}</option>
                {pros.map((p) => <option key={`${p.tid}|${p.oid}`} value={`${p.tid}|${p.oid}`}>{p.label}</option>)}
              </select>
            </div>
          )}
          <div className="dash-field">
            <label>{c.messageLabel}</label>
            <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} required />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy}>{c.sendMessage}</button>
        </form>
      </section>
    </>
  );
}

export function ClientProfilePage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const c = t.dashboard.client;

  if (editing) {
    return <ProfileForm space="client" onBack={() => setEditing(false)} />;
  }

  return (
    <>
      <header
        className="dash-page-head"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}
      >
        <div>
          <h1>{c.profileTitle}</h1>
          <p>{c.profileSubtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn"
            onClick={() => setEditing(true)}
            style={{ background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)' }}
          >
            <Pencil size={15} /> {t.dashboard?.edit || 'Modifier le profil'}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setPasswordOpen(true)}>
            <Lock size={15} /> {t.auth?.changePassword || 'Changer le mot de passe'}
          </button>
        </div>
      </header>

      <ProfileView user={user} space="client" />

      <DashModal
        open={passwordOpen}
        title={t.auth?.changePassword || 'Changer le mot de passe'}
        onClose={() => setPasswordOpen(false)}
      >
        <PasswordChangeForm />
      </DashModal>
    </>
  );
}

export function ClientNotificationsPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState('');
  const notifPg = usePagination(items, 6);

  const reload = () => clientApi.notifications().then((res) => setItems(res.notifications || [])).catch(() => setItems([]));

  useEffect(() => { reload(); }, []);

  const markAll = async () => {
    try {
      await clientApi.readNotifications();
      setMsg(d.saved);
      reload();
    } catch {
      setMsg('Error');
    }
  };

  return (
    <>
      <header className="dash-page-head dash-page-head--split">
        <div>
          <h1>{c.notificationsTitle || 'Notifications'}</h1>
          <p>{c.notificationsSubtitle || 'Vos dernières alertes.'}</p>
        </div>
        <button type="button" className="btn btn-outline" onClick={markAll}>{c.markAllRead || 'Tout marquer comme lu'}</button>
      </header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        {items.length === 0 ? (
          <p className="dash-empty">{d.empty}</p>
        ) : (
          <ul className="dash-notif-list">
            {notifPg.pageItems.map((n) => (
              <li key={n.id} className={n.readAt ? 'dash-notif-read' : 'dash-notif-unread'}>
                <strong>{n.title}</strong>
                <p>{n.body}</p>
                <small>{n.createdAt ? new Date(n.createdAt).toLocaleString() : '—'}</small>
              </li>
            ))}
          </ul>
        )}
        <Pagination page={notifPg.page} totalPages={notifPg.totalPages} total={notifPg.total} onChange={notifPg.setPage} />
      </section>
    </>
  );
}

export function ClientDocumentsPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    clientApi.orders().then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));
  }, []);

  const deliverables = orders.filter((o) => (o.documents || []).length > 0);
  const docsPg = usePagination(deliverables, 6);

  return (
    <>
      <header className="dash-page-head"><h1>{c.documentsTitle || 'Mes documents'}</h1><p>{c.documentsSubtitle || 'Documents soumis et livrés.'}</p></header>
      {deliverables.length === 0 ? (
        <p className="dash-empty">{c.noDocuments || 'Aucun document disponible.'}</p>
      ) : (
        docsPg.pageItems.map((o) => (
          <section key={o.id} className="dash-panel" style={{ marginBottom: 16 }}>
            <h3>{o.id} — {o.document}</h3>
            <p><StatusBadge status={o.status} labels={d.status} /> · {o.langs}</p>
            <ul className="dash-file-list">
              {(o.documents || []).map((f) => (
                <li key={f.id || f.name}>
                  <span>{f.name}</span>
                  {f.url && (
                    <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="dash-link" download>
                      Télécharger
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))
      )}
      <Pagination page={docsPg.page} totalPages={docsPg.totalPages} total={docsPg.total} onChange={docsPg.setPage} />
    </>
  );
}

export function ClientPaymentsPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [payments, setPayments] = useState([]);
  const paymentsPg = usePagination(payments, 6);

  useEffect(() => {
    clientApi.payments().then((res) => setPayments(res.payments || [])).catch(() => setPayments([]));
  }, []);

  return (
    <>
      <header className="dash-page-head">
        <h1>{c.paymentsTitle || 'Mes paiements'}</h1>
        <p>{c.paymentsSubtitle || 'Historique et suivi de vos paiements.'}</p>
      </header>
      <section className="dash-panel">
        <div className="dash-table-wrap">
          <table className="dash-table">
            <thead>
              <tr>
                <th>{c.colId || 'Réf.'}</th>
                <th>Commande</th>
                <th>Montant</th>
                <th>Statut</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {paymentsPg.pageItems.map((p) => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{p.orderCode || '—'}</td>
                  <td>{p.amount} {p.currency || 'TND'}</td>
                  <td><StatusBadge status={p.status} labels={d.status} /></td>
                  <td>{p.createdAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={paymentsPg.page} totalPages={paymentsPg.totalPages} total={paymentsPg.total} onChange={paymentsPg.setPage} />
      </section>
    </>
  );
}

export function ClientSupportPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) {
      setMsg(t.auth?.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const full = subject ? `**${subject}**\n\n${body}` : body;
      await clientApi.sendMessage({ body: full });
      setSubject('');
      setBody('');
      setMsg(d.saved);
    } catch (err) {
      setMsg(err.data?.error || d.empty);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="dash-page-head"><h1>{c.supportTitle || 'Support'}</h1><p>{c.supportSubtitle || 'Contactez l\'administrateur.'}</p></header>
      {msg && <p className="dash-toast">{msg}</p>}
      <section className="dash-panel">
        <form className="dash-form" onSubmit={send} noValidate style={{ maxWidth: 560 }}>
          <div className="dash-field">
            <label>{c.supportSubject || 'Sujet'}</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="dash-field">
            <label>{c.supportMessage || 'Message'}</label>
            <textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} required />
          </div>
          <button type="submit" className="btn btn-primary" disabled={busy}>{c.sendMessage || 'Envoyer'}</button>
        </form>
      </section>
    </>
  );
}
